/* Delegated listeners survive Shopify section reloads without duplicate handlers. */
(() => {
  if (window.contentSectionsReady) return;
  window.contentSectionsReady = true;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const carousels = new Map();
  const carouselSections = root => {
    const sections = [...root.querySelectorAll('[data-content-carousel]')];
    if (root.matches?.('[data-content-carousel]')) sections.unshift(root);
    return sections;
  };
  class ContentCarousel {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-content-track]');
      this.cards = [...this.track.children];
      this.arrows = section.querySelector('[data-content-arrows]');
      this.prev = section.querySelector('[data-content-prev]');
      this.next = section.querySelector('[data-content-next]');
      this.dots = section.querySelector('[data-content-dots]');
      this.pause = section.querySelector('[data-content-pause]');
      this.status = section.querySelector('[data-content-status]');
      this.cleanups = [];
      this.listen(this.track, 'scroll', () => this.update(), { passive: true });
      this.listen(window, 'resize', () => this.configure(), { passive: true });
      this.listen(section, 'pointerenter', () => { this.hovering = true; this.restartAutoplay(); });
      this.listen(section, 'pointerleave', () => { this.hovering = false; this.restartAutoplay(); });
      this.listen(section, 'focusin', () => { this.focused = true; this.restartAutoplay(); });
      this.listen(section, 'focusout', event => { this.focused = section.contains(event.relatedTarget); this.restartAutoplay(); });
      this.listen(document, 'visibilitychange', () => this.restartAutoplay());
      this.listen(section, 'loadedmetadata', () => this.update(), true);
      this.listen(section, 'load', () => this.update(), true);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.configure());
        this.observer.observe(this.track);
        this.cards.forEach(card => this.observer.observe(card));
        section.contentResizeObserver = this.observer;
      }
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motionQuery.addEventListener) this.listen(this.motionQuery, 'change', () => { this.update(); this.restartAutoplay(); });
      this.track.dataset.contentBound = 'true';
      this.configure();
    }
    listen(target, name, fn, options) {
      target.addEventListener(name, fn, options);
      this.cleanups.push(() => target.removeEventListener(name, fn, options));
    }
    configure() {
      const s = this.section.dataset;
      this.mobile = window.innerWidth < 750;
      this.enabled = s[this.mobile ? 'sliderMobile' : 'sliderDesktop'] === 'true';
      this.columns = this.mobile ? 1 : Math.max(1, window.innerWidth < 990 ? Number(s.columnsTablet) || 2 : Number(s.columnsDesktop) || 3);
      this.perMove = this.mobile ? 1 : Math.max(1, Number(s.perMove) || 1);
      this.loop = s.carouselType === 'loop';
      this.adaptive = this.enabled && this.columns === 1 && s[this.mobile ? 'mobileAdaptive' : 'desktopAdaptive'] === 'true';
      s.carouselMode = this.enabled ? 'slider' : 'grid';
      s.carouselArrows = s[this.mobile ? 'mobileArrows' : 'desktopArrows'] || 'under';
      s.carouselDots = s[this.mobile ? 'mobileDots' : 'desktopDots'] || 'hidden';
      s.adaptiveHeight = String(this.adaptive);
      s.carouselViewport = this.mobile ? 'mobile' : 'desktop';
      this.track.tabIndex = this.enabled ? 0 : -1;
      this.track.setAttribute('aria-roledescription', this.enabled ? 'carousel' : 'list');
      if (!this.enabled) this.track.scrollLeft = 0;
      this.update(true);
      this.restartAutoplay();
    }
    maxScroll() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    stepSize() {
      return (this.cards[0]?.getBoundingClientRect().width || this.track.clientWidth) + (parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0);
    }
    pagePositions() {
      const max = this.maxScroll();
      const step = Math.max(1, this.stepSize() * this.perMove);
      const positions = [0];
      for (let p = step; p < max - 2; p += step) positions.push(p);
      if (max > 2) positions.push(max);
      return positions;
    }
    update(rebuild = false) {
      const last = this.maxScroll();
      const movable = this.enabled && last > 2;
      this.arrows.hidden = !movable || this.section.dataset.carouselArrows === 'hidden';
      this.dots.hidden = !movable || this.section.dataset.carouselDots === 'hidden';
      this.prev.disabled = !movable || (!this.loop && this.track.scrollLeft < 3);
      this.next.disabled = !movable || (!this.loop && this.track.scrollLeft > last - 3);
      const pages = this.pagePositions();
      if (rebuild || this.dots.children.length !== pages.length) {
        this.dots.replaceChildren(...pages.map((left, index) => {
          const button = document.createElement('button');
          button.type = 'button'; button.dataset.contentPage = String(index);
          button.setAttribute('aria-label', `Go to testimonial page ${index + 1}`);
          button.setAttribute('aria-controls', this.track.id);
          return button;
        }));
      }
      const active = pages.reduce((closest, left, index) => Math.abs(left - this.track.scrollLeft) < Math.abs(pages[closest] - this.track.scrollLeft) ? index : closest, 0);
      [...this.dots.children].forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
      this.pause.hidden = !movable || this.section.dataset.autoplay !== 'true' || reducedMotion();
      this.pause.textContent = this.userPaused ? 'Play' : 'Pause';
      this.pause.setAttribute('aria-label', `${this.userPaused ? 'Play' : 'Pause'} testimonial autoplay`);
      this.track.style.height = '';
      if (this.adaptive) {
        const index = Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / Math.max(1, this.stepSize())));
        const height = this.cards[index]?.getBoundingClientRect().height;
        const style = getComputedStyle(this.track);
        if (height) this.track.style.height = `${height + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)}px`;
      }
    }
    goTo(left, announce = true) {
      if (!this.enabled) return;
      this.track.scrollTo({ left: Math.max(0, Math.min(this.maxScroll(), left)), behavior: reducedMotion() ? 'instant' : 'smooth' });
      if (announce) {
        const card = Math.min(this.cards.length, Math.round(left / Math.max(1, this.stepSize())) + 1);
        this.status.textContent = `Showing testimonial ${Math.max(1, card)} of ${this.cards.length}`;
      }
    }
    move(direction, announce = true) {
      const max = this.maxScroll();
      let target = this.track.scrollLeft + direction * this.stepSize() * this.perMove;
      if (this.loop && direction > 0 && this.track.scrollLeft >= max - 3) target = 0;
      if (this.loop && direction < 0 && this.track.scrollLeft <= 3) target = max;
      this.goTo(target, announce);
    }
    restartAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
      if (!this.enabled || this.maxScroll() < 3 || this.section.dataset.autoplay !== 'true' || reducedMotion() || this.userPaused || this.hovering || this.focused || document.hidden) return;
      const seconds = Math.max(1, Number(this.section.dataset.autoplaySpeed) || 5);
      this.timer = setInterval(() => {
        if (!this.loop && this.track.scrollLeft >= this.maxScroll() - 3) { clearInterval(this.timer); this.timer = null; return; }
        this.move(1, false);
      }, seconds * 1000);
    }
    destroy() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.cleanups.forEach(cleanup => cleanup());
      delete this.track.dataset.contentBound;
    }
  }
  const init = root => carouselSections(root).forEach(section => {
    if (!section.querySelector('[data-content-track]') || carousels.has(section)) return;
    carousels.set(section, new ContentCarousel(section));
  });
  document.addEventListener('click', event => {
    const control = event.target.closest('[data-content-prev], [data-content-next], [data-content-page], [data-content-pause]');
    if (!control) return;
    const carousel = carousels.get(control.closest('[data-content-carousel]'));
    if (!carousel || !carousel.enabled || control.disabled) return;
    if (control.hasAttribute('data-content-pause')) {
      carousel.userPaused = !carousel.userPaused; carousel.update(); carousel.restartAutoplay();
    } else {
      if (control.hasAttribute('data-content-page')) carousel.goTo(carousel.pagePositions()[Number(control.dataset.contentPage)] || 0);
      else carousel.move(control.hasAttribute('data-content-next') ? 1 : -1);
      carousel.restartAutoplay();
    }
  });
  document.addEventListener('keydown', event => {
    if (!event.target.matches?.('[data-content-track]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const carousel = carousels.get(event.target.closest('[data-content-carousel]'));
    if (!carousel?.enabled) return;
    event.preventDefault();
    if (event.key === 'Home' || event.key === 'End') carousel.goTo(event.key === 'Home' ? 0 : carousel.maxScroll());
    else carousel.move(event.key === 'ArrowRight' ? 1 : -1);
    carousel.restartAutoplay();
  });
  document.addEventListener('toggle', event => {
    if (!event.target.matches?.('.content-faq-item[open]')) return;
    const parent = event.target.closest('[data-content-faq]');
    if (parent?.dataset.singleOpen === 'true') {
      parent.querySelectorAll('.content-faq-item[open]').forEach(item => { if (item !== event.target) item.open = false; });
    }
  }, true);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    carouselSections(event.target).forEach(section => { carousels.get(section)?.destroy(); carousels.delete(section); });
  });
  document.addEventListener('shopify:block:select', event => {
    const detail = event.target.closest('details');
    if (detail) detail.open = true;
    const card = event.target.closest('.content-testimonial-card');
    if (card) {
      const carousel = carousels.get(card.closest('[data-content-carousel]'));
      if (carousel?.enabled) {
        carousel.userPaused = true;
        carousel.goTo(carousel.cards.indexOf(card) * carousel.stepSize());
        carousel.update(); carousel.restartAutoplay();
      }
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
